import type { KanbanReguaItem } from '../../api/carsApi';
import type { ServicoInacabado } from '../../interfaces/AtividadeCarro';

/**
 * Régua de esteiras de um veículo — regra ÚNICA (at-130, 23/10/2026).
 *
 * Até a at-130 a cascata de cores vivia inline no card do kanban (`home.tsx`). Ela passou a
 * ter três consumidores — o card do kanban, a placa do Telão e o modal "Informações do
 * atendimento" —, e três cópias divergiriam no primeiro ajuste: o mesmo veículo apareceria
 * "atual" numa tela e "realizado" noutra. Foi movida para cá SEM mudar comportamento.
 */

export type EstadoDoChip = 'atual' | 'realizada' | 'inacabada' | 'paralela' | 'prevista' | 'sistema';

/** Classe de cor do chip (`shared/ui/regua-esteiras.css`). */
export const COR_DO_ESTADO: Record<EstadoDoChip, string> = {
  atual: 'orange',
  realizada: 'green',
  inacabada: 'purple',
  paralela: 'teal',
  prevista: 'red',
  sistema: 'blue',
};

export const ROTULO_DO_ESTADO: Record<EstadoDoChip, string> = {
  atual: 'Atual',
  realizada: 'Realizada',
  inacabada: 'Serviço inacabado',
  paralela: 'Paralela atual',
  prevista: 'Prevista',
  sistema: 'Etapa do sistema',
};

/** Esteiras de controle que nunca entram no planejamento e nunca ficam "fora do plano". */
export const ESTEIRAS_IMPLICITAS = ['AGUARDANDO', 'AGUARDANDO_PROXIMA_ETAPA', 'AGUARDANDO_ENTREGA', 'ENTREGA'];

/** Uma esteira, do ponto de vista da régua de UM veículo. */
export interface BaseDoChip {
  id: string;
  /** Chave estável (`FUNILARIA`). No card antigo este campo chamava `name`. */
  name: string;
  nome: string;
  /** Já realizada: período aprovado OU serviço concluído (at-99, `realizadas`). */
  approved: boolean;
}

export interface ChipDaRegua extends BaseDoChip {
  estado: EstadoDoChip;
  cor: string;
  /** Existe na oficina, mas não está no plano deste veículo (chip esmaecido). */
  foraDoPlanejamento: boolean;
  /** Reserva a célula no fim da régua sem mostrar nada (Aguardando Próxima Etapa fora de uso). */
  oculto: boolean;
}

/** O que o veículo precisa ter para a régua ser derivada (campos do `GET /cars` e do `GET /cars/:id`). */
export interface VeiculoDaRegua {
  esteiraId?: string | null;
  status?: string | null;
  esteirasParalelas?: Array<{ esteiraId: string }> | null;
  servicosInacabados?: ServicoInacabado[] | null;
}

/**
 * Monta a base da régua a partir das esteiras e do item de `GET /cars/kanban-regua`.
 *
 * 🔴 at-99: `realizadas`, não `aprovadas`. Serviço concluído numa esteira aberta em PARALELO nunca
 * gera período no histórico de status, então `aprovadas` sozinho o mostraria como "planejado e
 * não feito" para sempre. O `?? item.aprovadas` mantém a tela funcionando contra um backend
 * anterior à at-99.
 */
export function montarBaseDaRegua(
  esteiras: Array<{ id: string; chave: string; nome: string }>,
  item: Pick<KanbanReguaItem, 'aprovadas' | 'realizadas'>,
): BaseDoChip[] {
  const realizadas = new Set(item.realizadas ?? item.aprovadas ?? []);
  return esteiras.map((est) => ({ id: est.id, name: est.chave, nome: est.nome, approved: realizadas.has(est.id) }));
}

/**
 * Deriva os chips da régua de um veículo.
 *
 * `planejadas` ausente (`undefined`) significa "planejamento ainda não carregado": nada fica
 * esmaecido. Array vazio significa "nada planejado".
 */
export function derivarReguaDoVeiculo(
  car: VeiculoDaRegua,
  base: BaseDoChip[],
  planejadas: string[] | undefined,
): ChipDaRegua[] {
  // AGUARDANDO_PROXIMA_ETAPA só é exibida quando é o estado atual. Quando oculta, vai
  // invisível para o FIM da régua: reserva a célula (cards com a mesma altura) sem deixar
  // buraco no meio do grid.
  const ehApeOculta = (s: BaseDoChip) =>
    s.name === 'AGUARDANDO_PROXIMA_ETAPA' && !(s.id === car.esteiraId || s.name === car.status);
  const ordenada = [...base.filter((s) => !ehApeOculta(s)), ...base.filter(ehApeOculta)];

  return ordenada.map((s) => {
    const ehAtual = s.id === car.esteiraId || s.name === car.status;
    const ehImplicita = ESTEIRAS_IMPLICITAS.includes(s.name);
    // at-99: esteira aberta em PARALELO — serviço acontecendo agora, fora da etapa do veículo.
    const emParalelo = (car.esteirasParalelas ?? []).some((p) => p.esteiraId === s.id);
    // Esteira já realizada nunca fica esmaecida (ex: ORCAMENTO, que é de sistema e não entra no
    // planejamento). ⚠️ at-99: paralela também não — abrir em paralelo não exige planejamento.
    const foraDoPlanejamento =
      !ehAtual &&
      !ehImplicita &&
      !s.approved &&
      !emParalelo &&
      planejadas !== undefined &&
      !planejadas.includes(s.id) &&
      !planejadas.includes(s.name);
    // at-46: serviço inacabado tem chip próprio (roxo) e vem antes de "atual"/"realizada".
    const temServicoInacabado = (car.servicosInacabados ?? []).some(
      (sv) => sv.estado === 'PAUSADA' && sv.esteiraId === s.id,
    );

    /**
     * A ordem da cascata é a regra, não estilo.
     *
     * ⚠️ at-99: a paralela vem DEPOIS do roxo de propósito. Uma paralela cujo serviço está
     * pausado continua aberta (ADR 0050), e a pendência é o que o Chefe precisa ver primeiro.
     * Ela vem ANTES do verde porque uma esteira já realizada pode estar aberta de novo em
     * paralelo, e aí o presente vale mais que o passado. Paralela e "atual" nunca colidem.
     */
    let estado: EstadoDoChip = 'prevista';
    if (temServicoInacabado) estado = 'inacabada';
    else if (ehAtual) estado = 'atual';
    else if (emParalelo) estado = 'paralela';
    else if (s.approved) estado = 'realizada';
    else if (ehImplicita) estado = 'sistema';

    return { ...s, estado, cor: COR_DO_ESTADO[estado], foraDoPlanejamento, oculto: ehApeOculta(s) };
  });
}
